import type { ProductData } from "@/mocks/products";
import { useProductAdvisor } from "@/components/feature/ProductAdvisor/ProductAdvisorContext";
import { Link } from "react-router-dom";

interface Props {
  product: ProductData;
}

export default function ProductDetailHero({ product }: Props) {
  const { lastOutcome, jumpToResults } = useProductAdvisor();

  return (
    <section className="relative pt-24 pb-10 md:pt-32 md:pb-14">
      <div className="absolute inset-0 bg-gradient-to-b from-graphite-850 via-graphite-900 to-graphite-900" />
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 xl:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <Link
              to="/products"
              className="text-sm text-silver-400 hover:text-emerald-soft transition-colors inline-flex items-center gap-1"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xs" />
              </span>
              Products
            </Link>
            <span className="text-silver-600">/</span>
            <span className="text-sm text-silver-300">{product.name}</span>
          </div>

          {lastOutcome && (
            <button
              onClick={() => lastOutcome && jumpToResults(lastOutcome)}
              className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-accent/30 bg-emerald-accent/10 text-xs font-medium text-emerald-accent hover:bg-emerald-accent/20 transition-colors cursor-pointer"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-compass-3-line text-xs" />
              </span>
              Back to Advisor Results
            </button>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-graphite-700 bg-graphite-800/50 mb-4">
                <i className={`${product.icon} text-sm text-emerald-accent`} />
                <span className="text-xs font-medium text-silver-300 tracking-wide">
                  {product.category}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 leading-tight">
                {product.name}
              </h1>
              <p className="text-lg md:text-xl text-emerald-soft font-medium mb-4">
                {product.tagline}
              </p>
              <p className="text-base text-silver-300 leading-relaxed max-w-xl mb-6">
                {product.fullDescription}
              </p>

              {product.stats && product.stats.length > 0 && (
                <div className="flex flex-wrap gap-6">
                  {product.stats.map((s) => (
                    <div key={s.label}>
                      <div className="text-2xl md:text-3xl font-bold text-emerald-accent">
                        {s.value}
                      </div>
                      <div className="text-xs text-silver-400 mt-0.5">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl overflow-hidden border border-graphite-700 bg-black">
              <img
                alt={`${product.name} hero visual`}
                className="w-full h-56 md:h-72 object-cover object-top"
                src={product.accentImage}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
